import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, List, Map } from 'immutable';
import { compact } from 'lodash';
import { CustomValidators } from 'ngx-custom-validators';
import { Observable } from 'rxjs';
import { v4 } from 'uuid';
import {
  DocumentReferenceTypesEnum,
  MAX_DOCUMENT_ITEM_QUANTITY,
  MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
  MAX_LENGTH_DOCUMENT_ITEM_NAME,
} from '../../../document-data/document-data.data';
import {
  DocumentItem,
  MarkupTypesEnum,
} from '../../../document-data/sub-models/document-item.model';
import { Reference } from '../../../document-data/sub-models/reference.model';
import { CONTENT_KEY } from '../../../entity-data/entity.model';
import { Expense } from '../../../expense-data/models/expense.model';
import {
  IFormMasterBuilder,
  IFormFieldErrorMessages,
} from '../../../form-master/form-master.interface';
import { ItemAutoCompleteTypesEnum } from '../../../item-data/item-data.data';
import { ItemDataService } from '../../../item-data/item-data.service';
import { Item } from '../../../item-data/item.model';
import { NavigationService } from '../../../navigation/navigation.service';
import { IAutoCompleteOptions } from '../../../shared/directives/auto-complete/auto-complete.interface';
import { MoneyPipe } from '../../../shared/pipes/money/money.pipe';
import { CalculatorService } from '../../../shared/services/calculator/calculator.service';
import {
  IDocumentItemOptionsInfo,
  IMarkupInfo,
} from '../document-item-options/document-item-options.component';
import { categoryTmpl, itemTmpl } from './auto-complete.tmpl';
import { map } from 'rxjs/operators';

@Injectable()
export class AddDocumentItemsBuilder implements IFormMasterBuilder {
  FORM_KEYS = DocumentItem.KEYS;

  constructor(
    private _formBuilder: FormBuilder,
    private _navigationService: NavigationService,
    private _itemDataService: ItemDataService,
    private _calculatorService: CalculatorService,
    private _translateService: TranslateService,
    private _moneyPipe: MoneyPipe
  ) {}

  buildForm(
    documentItem: DocumentItem,
    productNameEnabled: boolean
  ): FormGroup {
    const formKeys = this.FORM_KEYS;
    const formGroup: any = {
      [formKeys.CODE]: [documentItem.code, Validators.maxLength(100)],
      [formKeys.DESCRIPTION]: [
        documentItem.description,
        compact([
          productNameEnabled ? null : Validators.required,
          Validators.maxLength(MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION),
        ]),
      ],
      [formKeys.QTY]: [
        documentItem.quantity,
        [
          Validators.required,
          CustomValidators.range([0, MAX_DOCUMENT_ITEM_QUANTITY]),
        ],
      ],
      [formKeys.UNIT_PRICE]: [
        documentItem.unitPrice,
        [
          Validators.required,
          CustomValidators.range([-9999999999999, 9999999999999]),
        ],
      ],
    };

    if (productNameEnabled) {
      formGroup[formKeys.NAME] = [
        {
          value: documentItem.name,
          disabled:
            !!documentItem.referenceId && documentItem.isItemReference(),
        },
        [
          Validators.required,
          Validators.maxLength(MAX_LENGTH_DOCUMENT_ITEM_NAME),
        ],
      ];
    }

    return this._formBuilder.group(formGroup);
  }

  buildItemNameErrorMessages(
    productNameEnabled: boolean
  ): IFormFieldErrorMessages {
    const message = productNameEnabled
      ? 'ERROR.ITEM_NAME_REQUIRED'
      : 'ERROR.FIELD_REQUIRED';

    return {
      required: {
        message,
      },
    };
  }

  buildAutoCompleteOptions(
    documentItem: DocumentItem,
    showProductCode: boolean,
    separatePartsAndLabor: boolean,
    productNameEnabled: boolean,
    chooseMultipleCallback: (evt?: Event) => void
  ): any {
    const allFieldsEmpty: boolean = !(
      (showProductCode && documentItem.code) ||
      (productNameEnabled && documentItem.name) ||
      documentItem.description
    );
    const autoCompleteOptions = this._buildAutoCompleteOptions.bind(
      this,
      showProductCode,
      separatePartsAndLabor,
      productNameEnabled,
      chooseMultipleCallback
    );
    const nameAutoCompleteOptions = allFieldsEmpty
      ? autoCompleteOptions(
          (item: Item) => item.name || '',
          ItemAutoCompleteTypesEnum.names,
          DocumentItem.KEYS.NAME
        )
      : null;

    return {
      [this.FORM_KEYS.CODE]: allFieldsEmpty
        ? autoCompleteOptions(
            (item: Item) => item.code || '',
            ItemAutoCompleteTypesEnum.codes,
            DocumentItem.KEYS.CODE
          )
        : null,
      [this.FORM_KEYS.NAME]: nameAutoCompleteOptions,
      [this.FORM_KEYS.DESCRIPTION]: nameAutoCompleteOptions,
    };
  }

  buildDocumentItemProps(
    documentItemInfo: Map<string, any>,
    productNameEnabled: boolean,
    reference: Reference,
    markupInfo: IMarkupInfo,
    disableAutoTax: boolean
  ): Map<string, any> {
    const formKeys = this.FORM_KEYS;
    const documentItemProps = {
      [DocumentItem.KEYS.NAME]: productNameEnabled
        ? documentItemInfo.get(formKeys.NAME, null)
        : null,
      [DocumentItem.KEYS.DESCRIPTION]: documentItemInfo.get(
        formKeys.DESCRIPTION,
        null
      ),
      [DocumentItem.KEYS.UNIT_PRICE]: this.calculateFinalPrice(
        documentItemInfo.get(this.FORM_KEYS.UNIT_PRICE) || 0,
        markupInfo
      ),
      [DocumentItem.KEYS.UNIT_TYPE]: documentItemInfo.get(formKeys.UNIT_TYPE),
      [DocumentItem.KEYS.CODE]: documentItemInfo.get(formKeys.CODE, null),
      [DocumentItem.KEYS.QTY]: parseFloat(documentItemInfo.get(formKeys.QTY)),
      [DocumentItem.KEYS.REFERENCE]: reference,
    };

    if (disableAutoTax) {
      documentItemProps[DocumentItem.KEYS.APPLIED_TAXES] = [];
    }

    return fromJS(documentItemProps);
  }

  buildReference(reference: Item | Expense): Reference {
    return new Reference({
      [Reference.KEYS.TYPE]:
        reference.ENTITY_TYPE === Item.ENTITY_TYPE
          ? DocumentReferenceTypesEnum.product
          : DocumentReferenceTypesEnum.expense,
      [Reference.KEYS.ID]: reference.id || v4(),
      [Reference.KEYS.REFERENCE_LOCAL_ID]: reference.localId || v4(),
      [Reference.KEYS.REVISION_ID]: reference.revisionId || v4(),
    });
  }

  buildDocumentItemPropsFromDocumentItemOptionsInfo(
    documentItemInfo: Map<string, any>,
    documentItemOptionsInfo: IDocumentItemOptionsInfo
  ): Map<string, any> {
    return fromJS({
      [DocumentItem.KEYS.UNIT_PRICE]: this.calculateFinalPrice(
        documentItemInfo.get(this.FORM_KEYS.UNIT_PRICE) || 0,
        documentItemOptionsInfo.markup
      ),
    });
  }

  calculateFinalPrice(unitPrice: number, markupInfo: IMarkupInfo): number {
    const markupType: MarkupTypesEnum = markupInfo ? markupInfo.type : null;
    const markupValue = markupInfo ? markupInfo.value : 0;

    if (markupType === MarkupTypesEnum.amount) {
      return this._calculatorService.addWithRounding(unitPrice, markupValue);
    } else if (markupType === MarkupTypesEnum.percentage) {
      return this._calculatorService.percentageWithRounding(
        unitPrice,
        markupValue
      );
    } else {
      return unitPrice;
    }
  }

  private _buildAutoCompleteOptions(
    showProductCode: boolean,
    separatePartsAndLabor: boolean,
    productNameEnabled: boolean,
    chooseMultipleCallback: (evt: Event) => void,
    setFn: (item: Item) => any,
    autoCompleteType: ItemAutoCompleteTypesEnum,
    // should be one of DocumentItem.KEYS
    newItemFieldKey: string
  ): { autoComplete: IAutoCompleteOptions } {
    const chooseMultipleLabel = this._translateService.instant(
      'ACTION.CHOOSE_MULTIPLE'
    );
    const categoryLabel = this._translateService.instant(
      'LABEL.RECENTLY_CREATED_ITEMS'
    );
    const limit = 50;
    const autoComplete: IAutoCompleteOptions = {
      async: true,
      asyncCallback: (query: string): Observable<Item[]> =>
        this._itemDataService
          .autocompleteBasedOn(autoCompleteType, query)
          .pipe(map((items: List<Item>): Item[] => items.toArray())),
      placeholderElem: (query: string) => {
        return query
          ? new Item(fromJS({ [CONTENT_KEY]: { [newItemFieldKey]: query } }))
          : null;
      },
      itemTemplate: itemTmpl.bind(
        null,
        this._moneyPipe,
        this._translateService,
        newItemFieldKey,
        this._navigationService.getCurrentUrl(),
        showProductCode,
        separatePartsAndLabor,
        productNameEnabled
      ),
      limit,
      categoryTemplate: categoryTmpl.bind(
        null,
        categoryLabel,
        chooseMultipleLabel,
        chooseMultipleCallback
      ),
      setFn,
      hideListOnFullMatch: false,
    };

    return { autoComplete };
  }
}
